import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import ChecklistDetail from './ChecklistDetail';

function MyChecklists({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      const assignmentsData = await api.get('/assignments');
      // Filter to only show assignments TO this user
      const myAssignments = assignmentsData.filter(a => a.assignedToUserId === user.id);
      setAssignments(myAssignments);
      
      // Load templates for each assignment
      const templateIds = [...new Set(myAssignments.map(a => a.templateId))];
      const templatesData = {};
      for (const templateId of templateIds) {
        try {
          const template = await api.get(`/templates/${templateId}`);
          templatesData[templateId] = template;
        } catch (err) {
          console.error(`Failed to load template ${templateId}:`, err);
        }
      }
      setTemplates(templatesData);
    } catch (err) {
      console.error('Failed to load assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'IN_PROGRESS': return 'warning';
      case 'OVERDUE': return 'danger';
      default: return 'neutral';
    }
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ');
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (selectedAssignment) {
    return (
      <ChecklistDetail 
        assignment={selectedAssignment} 
        onBack={() => {
          setSelectedAssignment(null);
          loadAssignments();
        }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">My Checklists</h1>
        <p className="page-subtitle">View and complete your assigned checklists</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Active Assignments ({assignments.length})</h2>
        </div>

        {assignments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h3 className="empty-state-title">No Checklists Assigned</h3>
            <p className="empty-state-description">You don't have any active checklists at the moment.</p>
          </div>
        ) : (
          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {assignments.map((assignment) => {
                const template = templates[assignment.templateId];
                return (
                  <div
                    key={assignment.id}
                    style={{
                      padding: '1.5rem',
                      backgroundColor: 'var(--white)',
                      border: '1px solid var(--gray-300)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => setSelectedAssignment(assignment)}
                    data-testid={`assignment-${assignment.id}`}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                          {template?.name || 'Loading...'}
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                          {template?.description || ''}
                        </p>
                      </div>
                      <span className={`badge badge-${getStatusColor(assignment.status)}`}>
                        {getStatusLabel(assignment.status)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                      <div>
                        <span style={{ color: 'var(--gray-600)' }}>Started:</span>{' '}
                        <strong>{new Date(assignment.startDate).toLocaleDateString()}</strong>
                      </div>
                      {assignment.targetCompletionDate && (
                        <div>
                          <span style={{ color: 'var(--gray-600)' }}>Due:</span>{' '}
                          <strong>{new Date(assignment.targetCompletionDate).toLocaleDateString()}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyChecklists;
